import { useCallback, useEffect, useState } from "react";
import {
  addAcaoFavorita,
  findAcoesAnosCount,
  findAcoesFavoritas,
  findAcoesPorAno,
  findAcoesSituacoes,
  findAllAcoesQualificacao,
  inactivateAcao,
  removeAcaoFavorita,
} from "../../../../API/apiAcoes";
import {
  AcaoAnoCount,
  AcaoQualificacao,
  AcoesPorAno,
  AcaoSituacaoCount,
} from "../../../../types/acao";
import { useComplementoAcao } from "./useComplementoAcao";

export type AcaoListTab = "todas" | "por-ano" | "favoritas";
export type AcaoOrder = "mais_recentes" | "mais_antigas" | "nome_az" | "nome_za" | "data_inicio" | "data_termino";

interface FiltrosListarAcoes {
  searchNome: string;
  searchTipologia: number | null;
  tema: number | null;
  searchModalidade: number | null;
  searchDataInicio: string;
  searchDataFim: string;
}

const filtrosIniciais: FiltrosListarAcoes = {
  searchNome: "",
  searchTipologia: null,
  tema: null,
  searchModalidade: null,
  searchDataInicio: "",
  searchDataFim: "",
};

const sortAcoes = (items: AcaoQualificacao[], ordenacao: AcaoOrder) => {
  const sorted = [...items];

  return sorted.sort((first, second) => {
    if (ordenacao === "nome_az" || ordenacao === "nome_za") {
      return first.nome.localeCompare(second.nome, "pt-BR") * (ordenacao === "nome_az" ? 1 : -1);
    }

    const field = ordenacao === "data_termino" ? "data_fim" : ordenacao === "data_inicio" ? "data_inicio" : "created_at";
    const direction = ordenacao === "mais_recentes" ? -1 : 1;
    return (new Date(first[field]).getTime() - new Date(second[field]).getTime()) * direction;
  });
};

export const useAcao = () => {
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [acoes, setAcoes] = useState<AcaoQualificacao[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<AcaoListTab>("todas");
  const [ordenacao, setOrdenacao] = useState<AcaoOrder>("mais_recentes");
  const [selectedYear, setSelectedYear] = useState<number | null>(new Date().getFullYear());
  const [selectedSituacao, setSelectedSituacao] = useState<string | null>(null);
  const [selectedTipoOferta, setSelectedTipoOferta] = useState<string | null>(null);
  const [anos, setAnos] = useState<AcaoAnoCount[]>([]);
  const [situacoes, setSituacoes] = useState<AcaoSituacaoCount[]>([]);
  const [ofertaCounts, setOfertaCounts] = useState({ OFERTA_ESTADUAL: 0, OFERTA_EXTERNA: 0 });
  const [quickFiltersLoaded, setQuickFiltersLoaded] = useState(false);
  const [actionsLoaded, setActionsLoaded] = useState(false);
  const [acoesPorAno, setAcoesPorAno] = useState<AcoesPorAno[]>([]);
  const [filtros, setFiltros] = useState<FiltrosListarAcoes>(filtrosIniciais);
  const [filtrosAplicados, setFiltrosAplicados] = useState<FiltrosListarAcoes>(filtrosIniciais);
  const { loadingComplementos, complementosResolvidos, tipologias, modalidadesAcao, temas } = useComplementoAcao();

  const loadQuickFilters = useCallback(async () => {
    const [anosResult, situacoesResult, estadualResult, externaResult] = await Promise.allSettled([
      findAcoesAnosCount(),
      findAcoesSituacoes(),
      findAllAcoesQualificacao(1, 1, undefined, undefined, undefined, undefined, undefined, undefined, { tipoOferta: "OFERTA_ESTADUAL" }),
      findAllAcoesQualificacao(1, 1, undefined, undefined, undefined, undefined, undefined, undefined, { tipoOferta: "OFERTA_EXTERNA" }),
    ]);

    if (anosResult.status === "fulfilled") {
      const orderedYears = [...anosResult.value].sort((first, second) => second.ano - first.ano);
      setAnos(orderedYears);
      setSelectedYear((currentYear) => (
        orderedYears.length > 0 && !orderedYears.some((item) => item.ano === currentYear)
          ? orderedYears[0].ano
          : currentYear
      ));
    }

    setOfertaCounts({
      OFERTA_ESTADUAL: estadualResult.status === "fulfilled" ? estadualResult.value.pagination?.total ?? 0 : 0,
      OFERTA_EXTERNA: externaResult.status === "fulfilled" ? externaResult.value.pagination?.total ?? 0 : 0,
    });

    if (situacoesResult.status === "fulfilled") {
      setSituacoes(situacoesResult.value);
    }

    setQuickFiltersLoaded(true);
  }, []);

  useEffect(() => {
    loadQuickFilters();
  }, [loadQuickFilters]);

  const getAnoActions = useCallback(async () => {
    const groups = await findAcoesPorAno();
    setAcoesPorAno(groups);
    return groups;
  }, []);

  const buscaAcoes = useCallback(async () => {
    setLoading(true);
    setMsgError(null);
    try {
      if (activeTab === "por-ano") {
        const groups = acoesPorAno.length > 0 ? acoesPorAno : await getAnoActions();
        const yearActions = groups.find((group) => group.ano === (selectedYear ?? new Date().getFullYear()))?.acoes ?? [];
        const filtered = yearActions.filter((acao) => {
          const byName = !filtrosAplicados.searchNome || acao.nome.toLocaleLowerCase("pt-BR").includes(filtrosAplicados.searchNome.toLocaleLowerCase("pt-BR"));
          const byTipologia = !filtrosAplicados.searchTipologia || acao.tipologia_id === filtrosAplicados.searchTipologia;
          const byModalidade = !filtrosAplicados.searchModalidade || acao.modalidade_id === filtrosAplicados.searchModalidade;
          const byTema = !filtrosAplicados.tema || acao.temas?.some((tema) => tema.tema_id === filtrosAplicados.tema);
          const bySituacao = !selectedSituacao || acao.situacao === selectedSituacao;
          const byStart = !filtrosAplicados.searchDataInicio || acao.data_inicio >= filtrosAplicados.searchDataInicio;
          const byEnd = !filtrosAplicados.searchDataFim || acao.data_fim <= filtrosAplicados.searchDataFim;
          return byName && byTipologia && byModalidade && byTema && bySituacao && byStart && byEnd;
        });
        const ordered = sortAcoes(filtered, ordenacao);
        const pages = Math.max(1, Math.ceil(ordered.length / perPage));
        const page = Math.min(currentPage, pages);
        setAcoes(ordered.slice((page - 1) * perPage, page * perPage));
        setTotalItems(ordered.length);
        setTotalPages(pages);
        if (page !== currentPage) setCurrentPage(page);
        return;
      }

      const listFilters = {
        ano: selectedYear ?? undefined,
        situacao: selectedSituacao ?? undefined,
        tipoOferta: selectedTipoOferta ?? undefined,
        ordenacao,
      };
      const result = activeTab === "favoritas"
        ? await findAcoesFavoritas(currentPage, perPage, listFilters)
        : await findAllAcoesQualificacao(
            currentPage, perPage, filtrosAplicados.searchNome || undefined,
            filtrosAplicados.searchModalidade ?? undefined, filtrosAplicados.searchTipologia ?? undefined,
            filtrosAplicados.tema ?? undefined, filtrosAplicados.searchDataInicio || undefined,
            filtrosAplicados.searchDataFim || undefined, listFilters,
          );
      setAcoes(result.acoes ?? []);
      setTotalItems(result.pagination?.total ?? 0);
      setTotalPages(result.pagination?.last_page ?? 1);
    } catch {
      setMsgError("Nao foi possivel listar as acoes de qualificacao.");
      setAcoes([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setActionsLoaded(true);
    }
  }, [activeTab, acoesPorAno, currentPage, filtrosAplicados, getAnoActions, ordenacao, perPage, selectedSituacao, selectedTipoOferta, selectedYear]);

  useEffect(() => {
    buscaAcoes();
  }, [buscaAcoes]);

  useEffect(() => {
    if (!msgSuccess && !msgError) return;
    const timer = window.setTimeout(() => {
      setMsgSuccess(null);
      setMsgError(null);
    }, 3500);
    return () => window.clearTimeout(timer);
  }, [msgError, msgSuccess]);

  const atualizarFiltro = (nome: keyof FiltrosListarAcoes, valor: string | number | null) => setFiltros((previous) => ({ ...previous, [nome]: valor }));
  const pesquisar = () => { setCurrentPage(1); setFiltrosAplicados(filtros); };
  const limparFiltros = () => {
    setFiltros(filtrosIniciais);
    setFiltrosAplicados(filtrosIniciais);
    setSelectedSituacao(null);
    setSelectedTipoOferta(null);
    setCurrentPage(1);
  };
  const changeTab = (tab: AcaoListTab) => { setActiveTab(tab); setCurrentPage(1); };
  const selectYear = (year: number) => { setSelectedYear(year); setCurrentPage(1); };
  const toggleSituacao = (situacao: string) => { setSelectedSituacao((previous) => previous === situacao ? null : situacao); setCurrentPage(1); };
  const toggleTipoOferta = (tipoOferta: string) => { setSelectedTipoOferta((previous) => previous === tipoOferta ? null : tipoOferta); setCurrentPage(1); };
  const changeOrdenacao = (value: AcaoOrder) => { setOrdenacao(value); setCurrentPage(1); };

  const inativarAcao = async (acaoId: string): Promise<boolean> => {
    setLoadingConfirm(true);
    try {
      const response = await inactivateAcao(acaoId);
      if (response?.success === false) {
        setMsgError(response?.message ?? "Nao foi possivel inativar a acao.");
        return false;
      }
      setMsgSuccess("Acao inativada com sucesso!");
      await Promise.all([buscaAcoes(), loadQuickFilters()]);
      return true;
    } catch {
      setMsgError("Nao foi possivel inativar a acao.");
      return false;
    } finally {
      setLoadingConfirm(false);
    }
  };

  const toggleFavorita = async (acao: AcaoQualificacao) => {
    try {
      if (acao.favorita) {
        await removeAcaoFavorita(acao.id);
        setMsgSuccess("Acao removida dos favoritos.");
      } else {
        await addAcaoFavorita(acao.id);
        setMsgSuccess("Acao adicionada aos favoritos.");
      }
      setAcoes((previous) => previous.map((item) => (
        item.id === acao.id ? { ...item, favorita: !acao.favorita } : item
      )));
    } catch {
      setMsgError("Nao foi possivel atualizar os favoritos.");
    }
  };

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalItems);

  return {
    msgError, msgSuccess, loading: loading || loadingComplementos,
    pageLoading: !actionsLoaded || !quickFiltersLoaded || !complementosResolvidos || loading || loadingComplementos,
    loadingConfirm, acoes,
    tipologias, modalidades: modalidadesAcao, temas, currentPage, perPage, totalPages, totalItems,
    rangeStart, rangeEnd, filtros, anos, situacoes, ofertaCounts, activeTab, ordenacao, selectedYear,
    selectedSituacao, selectedTipoOferta, setCurrentPage, setPerPage, atualizarFiltro,
    pesquisar, limparFiltros, changeTab, selectYear, toggleSituacao, toggleTipoOferta,
    changeOrdenacao, inativarAcao, toggleFavorita,
  };
};
